import { useEffect, useState } from "react";
import { Boxes, Plus, ChevronLeft, ChevronRight, ArrowUpDown, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const LOW = 5;
const tone = (v) => (v > LOW ? "text-emerald-400" : v > 0 ? "text-amber-400" : "text-rose-400");

function StockRow({ driver, cat, reload }) {
  const [qty, setQty] = useState("");
  const value = driver.stock[cat.category] ?? 0;
  const send = async (payload, msg) => {
    try { await api.patch(`/admin/drivers/${driver.id}/stock`, { category: cat.category, ...payload }); toast.success(msg); setQty(""); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const amount = () => parseFloat(String(qty).replace(",", "."));
  const adjust = (sign) => {
    const a = amount();
    if (!a || a <= 0) { toast.error("Indiquez une quantité valide"); return; }
    send({ delta: sign * a }, `${driver.name} · ${cat.category} : ${sign > 0 ? "+" : "-"}${a}`);
  };
  const define = () => {
    const a = amount();
    if (isNaN(a)) { toast.error("Indiquez une quantité valide"); return; }
    send({ set: a }, `${driver.name} · ${cat.category} défini à ${a}`);
  };
  const tid = `${driver.id}-${cat.category}`;
  return (
    <div data-testid={`driver-stock-row-${tid}`} className="flex flex-wrap items-center gap-2 py-2 border-b border-amber-500/5 last:border-0">
      <div className="flex-1 min-w-[110px]">
        <p className="text-sm text-slate-100">{cat.category}</p>
        <p className={`text-lg font-bold font-mono ${tone(value)}`} data-testid={`driver-stock-value-${tid}`}>{value}</p>
        {value < 0 && <p className="text-[10px] text-rose-300">À réapprovisionner : {Math.abs(value)}</p>}
      </div>
      <Input data-testid={`driver-stock-input-${tid}`} inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="Qté"
        className="w-20 h-9 bg-[#0F1320] border-amber-500/20 text-slate-100 font-mono" />
      <Button data-testid={`driver-stock-add-${tid}`} onClick={() => adjust(1)} size="sm" className="btn-gold rounded-lg h-9 px-2.5"><Plus className="w-4 h-4" /> Ajouter</Button>
      <Button data-testid={`driver-stock-remove-${tid}`} onClick={() => adjust(-1)} size="sm" variant="outline" className="rounded-lg h-9 px-2.5 border-rose-500/40 text-rose-300 hover:bg-rose-500/10">Retirer</Button>
      <Button data-testid={`driver-stock-set-${tid}`} onClick={define} size="sm" variant="outline" className="rounded-lg h-9 px-2.5 border-amber-500/40 text-amber-200 hover:bg-amber-500/10">Définir</Button>
    </div>
  );
}

function DriverChips({ drivers, selected, onSelect, reload }) {
  const [editing, setEditing] = useState(false);
  const [list, setList] = useState(drivers);
  useEffect(() => { setList(drivers); }, [drivers]);
  const move = async (i, dir) => {
    const next = [...list];
    [next[i], next[i + dir]] = [next[i + dir], next[i]];
    setList(next);
    try { await api.put("/admin/drivers/stock-order", { ids: next.map((d) => d.id) }); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setList(drivers); }
  };
  return (
    <div className="mb-3">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] font-mono uppercase tracking-wider text-slate-500">Choisir un livreur</p>
        {list.length > 1 && (
          <button data-testid="stock-reorder-toggle" onClick={() => setEditing(!editing)} className="text-xs text-amber-300 flex items-center gap-1">
            {editing ? <><Check className="w-3 h-3" /> Terminé</> : <><ArrowUpDown className="w-3 h-3" /> Réorganiser</>}
          </button>
        )}
      </div>
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
        {list.map((d, i) => (
          <div key={d.id} className={`shrink-0 flex items-center rounded-full border transition-colors ${selected === d.id ? "btn-gold border-transparent" : "bg-[#161926] border-amber-500/20 text-slate-300"}`}>
            {editing && i > 0 && <button data-testid={`stock-move-left-${d.id}`} onClick={() => move(i, -1)} className="pl-2"><ChevronLeft className="w-4 h-4" /></button>}
            <button data-testid={`stock-driver-chip-${d.id}`} onClick={() => onSelect(d.id)} className="px-3 py-1.5 text-xs font-semibold">
              {d.name}{!d.active && " · inactif"}
            </button>
            {editing && i < list.length - 1 && <button data-testid={`stock-move-right-${d.id}`} onClick={() => move(i, 1)} className="pr-2"><ChevronRight className="w-4 h-4" /></button>}
          </div>
        ))}
      </div>
    </div>
  );
}

export const StockPanel = ({ stock, reload }) => {
  const [selected, setSelected] = useState(stock.drivers[0]?.id);
  const driver = stock.drivers.find((d) => d.id === selected) || stock.drivers[0];
  if (stock.categories.length === 0) {
    return <div className="glass rounded-2xl p-5 mt-5 text-sm text-slate-400" data-testid="stock-empty">Aucune catégorie : créez des produits dans le Catalogue.</div>;
  }
  return (
    <div className="glass rounded-2xl p-5 mt-5 border border-amber-500/15" data-testid="stock-panel">
      <h3 className="text-lg font-semibold text-slate-200 mb-1 flex items-center gap-2"><Boxes className="w-4 h-4 text-amber-400" /> Stock des livreurs</h3>
      <p className="text-[12px] text-slate-400 mb-4">Chaque livraison déduit le stock du livreur qui livre. Le client ne voit jamais le stock.</p>
      <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-4 mb-4">
        <p className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-2">Total détenu par les livreurs</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {stock.categories.map((c) => (
            <div key={c.category} data-testid={`stock-total-${c.category}`}>
              <p className="text-xs text-slate-300 truncate">{c.category} <span className="text-slate-500">· {c.product_count} produit{c.product_count > 1 ? "s" : ""}</span></p>
              <p className={`text-2xl font-bold font-mono ${tone(c.total)}`}>{c.total}</p>
            </div>
          ))}
        </div>
      </div>
      {driver ? (
        <>
          <DriverChips drivers={stock.drivers} selected={driver.id} onSelect={setSelected} reload={reload} />
          <div data-testid={`driver-stock-${driver.id}`} className="bg-[#161926] rounded-xl p-4 border border-amber-500/10">
            <p className="text-sm font-semibold text-slate-100 mb-1">Stock de {driver.name}</p>
            {stock.categories.map((c) => <StockRow key={c.category} driver={driver} cat={c} reload={reload} />)}
          </div>
        </>
      ) : <p className="text-sm text-slate-500 text-center py-3">Aucun livreur.</p>}
    </div>
  );
};
